import React, { useState, useEffect } from "react";
import { Modal, Box, Typography } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import SpriteAnimator from "../components/gamePlay/SpriteAnimator";
import NewCardsPreview from "./NewCardsPreview";
import { useCardStore } from "../@shared/store/useCardsStore";
import { generateCardPack } from "../@shared/game/cardPackEngine";
import packImage from "../@shared/assets/cardpack_basic.png";
import raysImage from "../@shared/assets/itemLightRays.png";
import { useAuthStore } from "../@shared/store/useAuthStore";

const MotionBox = motion(Box);
const MotionImg = motion("img");

const PackOpeningModal = ({ visible, onClose, reward }) => {
  const { userData } = useAuthStore();
  const { infinityCards, characterCards } = useCardStore();
  const [pack, setPack] = useState([]);
  const [opened, setOpened] = useState(false);
  const [sparkles, setSparkles] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const packControls = useAnimation();
  const raysControls = useAnimation();
  const pulseControls = useAnimation();

  // Generate card pack
  useEffect(() => {
    if (visible) {
      if (reward) {
        setPack(
          [...reward].map((card) => {
            if (card?.rarity) {
              return {
                ...card,
                type: "character",
              };
            } else {
              return {
                ...card,
                type: "Infinity",
              };
            }
          })
        );
      } else if (
        infinityCards.length &&
        characterCards.length &&
        userData?.roles.includes("admin")
      ) {
        const icards = infinityCards.map((card) => ({
          ...card,
          type: "Infinity",
        }));
        const ccards = characterCards.map((card) => ({
          ...card,
          type: "character",
        }));
        setPack(generateCardPack(icards, ccards));
      }
    }
  }, [visible, infinityCards, characterCards, reward]);

  // Reset on close
  useEffect(() => {
    if (!visible) {
      setOpened(false);
      setImagesLoaded(false);
      packControls.set({ scale: 1, opacity: 1 });
      raysControls.set({ rotate: 0, scale: 1 });
      setPack([]);
      setSparkles([]);
    }
  }, [visible]);

  // Trigger animations when ready
  useEffect(() => {
    if (visible && pack.length > 0 && imagesLoaded && !opened) {
      pulseControls.start({
        scale: [1, 1.1, 1],
        transition: { repeat: Infinity, duration: 1.6, ease: "easeInOut" },
      });
      raysControls.start({
        rotate: 360,
        transition: { repeat: Infinity, duration: 6, ease: "linear" },
      });
    }
  }, [visible, pack.length, imagesLoaded, opened]);

  // Sparkle generation
  useEffect(() => {
    if (!visible || opened) return;

    const generateSparkles = () => {
      const count = 1;
      const newSparkles = Array.from({ length: count }).map((_, i) => {
        const sparkleId = `sparkle-${Date.now()}-${i}`;
        const sparkle = {
          id: sparkleId,
          top: `${Math.floor(Math.random() * 89) + 1}%`,
          left: `${Math.floor(Math.random() * 89) + 1}%`,
          delay: 0,
          fps: 16,
        };

        // Auto-remove after 1.2 seconds
        setTimeout(() => {
          setSparkles((prev) => prev.filter((s) => s.id !== sparkleId));
        }, 1000); // Adjust to match animation duration

        return sparkle;
      });

      setSparkles((prev) => [...prev, ...newSparkles]);
    };

    generateSparkles();
    const interval = setInterval(generateSparkles, 750);

    return () => clearInterval(interval);
  }, [visible, opened]);

  const handleOpenPack = async () => {
    await packControls.start({
      scale: [1.1, 1.3, 1, 2, 1.5],
      opacity: [1, 1, 1, 0],
      transition: { duration: 1.5 },
    });
    setOpened(true);
  };

  const handleImageLoad = () => {
    setImagesLoaded(true);
  };

  return (
    <Modal open={visible} onClose={onClose}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        {!opened ? (
          pack.length > 0 ? (
            <MotionBox
              onClick={handleOpenPack}
              animate={packControls}
              initial={{ scale: 1, opacity: 1 }}
              whileHover={{ scale: 1.05 }}
              sx={{
                width: 220,
                height: 320,
                position: "relative",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Sparkles */}
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                {sparkles.map(({ id, top, left, delay, fps }) => (
                  <Box
                    key={id}
                    sx={{
                      position: "absolute",
                      top,
                      left,
                      width: 64,
                      height: 64,
                      overflow: "hidden",
                    }}
                  >
                    <SpriteAnimator
                      animationName="shinefx"
                      frameHeight={64}
                      frameCount={12}
                      fps={fps}
                      delay={delay}
                      loop={false}
                      resetOnFinish={true}
                      style={{ width: 64, height: 64 }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Light Rays */}
              <MotionImg
                src={raysImage}
                onLoad={handleImageLoad}
                animate={raysControls}
                style={{
                  position: "absolute",
                  width: 500,
                  height: 500,
                  zIndex: 0,
                  opacity: 0.7,
                }}
              />

              {/* Pack Image */}
              <MotionImg
                src={packImage}
                onLoad={handleImageLoad}
                animate={pulseControls}
                style={{
                  width: "100%",
                  height: "100%",
                  zIndex: 1,
                }}
              />
            </MotionBox>
          ) : (
            <Typography color="white" fontSize={18}>
              Loading Pack...
            </Typography>
          )
        ) : (
          <Box
            sx={{
              width: "80%",
              maxWidth: 420,
              height: "80%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NewCardsPreview onComplete={onClose} cards={pack} />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default PackOpeningModal;
